"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { MoreVertical, Plus, Pencil, Trash2, User } from "lucide-react";
import { useBooking } from "@/contexts/BookingContext";
import {
  BookingGuest,
  BookingStep,
  MAX_ADDITIONAL_GUESTS,
} from "@/types/booking";
import { Button } from "@/components/ui/button";
import CartSummary from "@/components/booking/CartSummary";
import InitialsAvatar from "@/components/booking/InitialsAvatar";

/**
 * Group-booking hub: lists Me + guests with service counts, Add Guest,
 * and continue into staff / date-time selection.
 */
export default function GuestsAndServices() {
  const {
    guests,
    selectedServices,
    businessInfo,
    handleNextStep,
    addGuest,
    removeGuest,
    renameGuest,
    setActiveGuestId,
    ensureMeGuest,
    guestLabel,
    isMobile,
  } = useBooking();

  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ensureMeGuest();
  }, [ensureMeGuest]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setMenuOpenFor(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const orderedGuests = useMemo(
    () => [...guests].sort((a, b) => a.position - b.position),
    [guests]
  );

  const serviceCountFor = (guest: BookingGuest) =>
    selectedServices.filter((s) => s.guestId === guest.id).length;

  const canAddGuest =
    orderedGuests.filter((g) => g.position > 1).length < MAX_ADDITIONAL_GUESTS;

  const handleAddGuest = () => {
    const guest = addGuest();
    if (guest) {
      handleNextStep(BookingStep.INITIAL);
    }
  };

  const handleEdit = (guest: BookingGuest) => {
    setMenuOpenFor(null);
    setActiveGuestId(guest.id);
    handleNextStep(BookingStep.INITIAL);
  };

  const handleStartRename = (guest: BookingGuest) => {
    setMenuOpenFor(null);
    setRenamingId(guest.id);
    setRenameValue(guest.name || "");
  };

  const handleCommitRename = (guestId: string) => {
    renameGuest(guestId, renameValue);
    setRenamingId(null);
    setRenameValue("");
  };

  const handleRemove = (guest: BookingGuest) => {
    setMenuOpenFor(null);
    removeGuest(guest.id);
  };

  const handleContinue = () => {
    setActiveGuestId(null);
    const nextStep = businessInfo?.allowServiceStaffSelection
      ? BookingStep.STAFF_SELECTION
      : BookingStep.TIME_AND_DATE;
    handleNextStep(nextStep);
  };

  const meHasServices = orderedGuests.some(
    (g) => g.position === 1 && serviceCountFor(g) > 0
  );

  return (
    <div className="min-h-screen bg-grey-25 pb-28 lg:pb-8">
      <div className="max-w-6xl mx-auto px-4 py-6 lg:py-10">
        <div className="lg:flex lg:gap-8 lg:items-start">
          <div className="flex-1 space-y-4">
            <div>
              <h1 className="text-2xl font-semibold text-grey-900 font-heading">
                Add guests and services
              </h1>
              <p className="mt-1 text-sm text-grey-600">
                Book for yourself and up to {MAX_ADDITIONAL_GUESTS} guests.
                Services are scheduled one after another.
              </p>
            </div>

            <div className="space-y-3">
              {orderedGuests.map((guest) => {
                const count = serviceCountFor(guest);
                const label = guestLabel(guest);
                const isRenaming = renamingId === guest.id;

                return (
                  <div
                    key={guest.id}
                    className="bg-white border border-grey-75 rounded-lg px-4 py-3 flex items-center gap-3"
                  >
                    <InitialsAvatar label={label} />
                    <div className="flex-1 min-w-0">
                      {isRenaming ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleCommitRename(guest.id);
                          }}
                          className="flex gap-2 items-center"
                        >
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => handleCommitRename(guest.id)}
                            placeholder={
                              guest.position === 1
                                ? "Me"
                                : `Guest ${guest.position}`
                            }
                            className="w-full border border-grey-100 rounded px-2 py-1 text-sm"
                            aria-label={`Rename ${label}`}
                          />
                        </form>
                      ) : (
                        <>
                          <p className="font-semibold text-grey-900 truncate">
                            {label}
                          </p>
                          <p className="text-sm text-grey-600">
                            {count} {count === 1 ? "service" : "services"}
                          </p>
                        </>
                      )}
                    </div>

                    <div className="relative" ref={menuOpenFor === guest.id ? menuRef : undefined}>
                      <button
                        type="button"
                        aria-label={`Options for ${label}`}
                        onClick={() =>
                          setMenuOpenFor((id) =>
                            id === guest.id ? null : guest.id
                          )
                        }
                        className="p-2 rounded-full hover:bg-grey-50 text-grey-600"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>

                      {menuOpenFor === guest.id && (
                        <div className="absolute right-0 bottom-full mb-1 w-44 bg-white rounded-lg shadow-lg border border-grey-100 py-1 z-50">
                          <button
                            type="button"
                            onClick={() => handleEdit(guest)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-grey-50 text-grey-800"
                          >
                            <Pencil className="w-4 h-4" />
                            Edit services
                          </button>
                          {guest.position > 1 && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleStartRename(guest)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-grey-50 text-grey-800"
                              >
                                <User className="w-4 h-4" />
                                Rename
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemove(guest)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-grey-50 text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                                Remove
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              type="button"
              variant="outline"
              disabled={!canAddGuest}
              onClick={handleAddGuest}
              className="w-full sm:w-auto border-green-300 text-green-300 hover:bg-green-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add guest
            </Button>
          </div>

          <div className="hidden lg:block w-96 mt-8 lg:mt-0">
            <div className="sticky top-6">
              <CartSummary
                showButton
                customButton={{
                  text: "Continue",
                  onClick: handleContinue,
                  className: "w-full bg-green-300 text-white",
                  disabled: !meHasServices,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-grey-100 lg:hidden">
          <Button
            onClick={handleContinue}
            disabled={!meHasServices}
            className="w-full bg-green-300 text-white"
          >
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}
